import * as React from "react";
import { Viewport, ViewSmall, ViewLarge } from "bernie-view";
import { inject, observer } from "mobx-react";

import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkHeading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";
import { AnalyticsConfig } from "uitk-react-utils";

import { HotelCard } from "../components/HotelCard";
import { ViewAllHotelsButton } from "../components/ViewAllHotelsButton";
import { CardContent } from "../components/CardContent";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { ViewCarouselProps } from "../typings";
import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import { hotelStructuredData } from "components/flexComponents/Hotels/utils/getHotelStructuredData";
import { SchemaOrgScript } from "components/utility/SchemaOrg/SchemaOrgScript";
import { LegalText } from "../components/LegalText";
import { HotelsContext } from "components/flexComponents/Hotels/components/HotelsContext";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";

@inject("thumbor", "analytics", "hotels", "flexModuleModelStore", "context")
@observer
export class ViewCarousel extends React.Component<ViewCarouselProps> {
  public componentDidMount(): void {
    const model = this.props.flexModuleModelStore.getModel(
      this.props.flexTemplateNode.id
    ) as HotelsFlexModuleResult | null;

    if (!model) {
      return;
    }

    const hotelsStore = this.props.hotels;
    const locationId = model.destinationId ?? this.props.context?.searchContext?.location?.id;
    const hotels: Hotel[] = model.hotels;
    const hotelsOnMap: boolean = typeof model.showMarkersOnMap === "undefined" || model.showMarkersOnMap === true;

    if (hotelsStore && hotelsOnMap && hotels && hotels.length > 0) {
      hotelsStore.fetchMapHotelsById(hotels, this.props.context, locationId);
    }
  }

  // This should be removed after UITK Carousel is fixed. (https://jira.expedia.biz/browse/UDS-577)
  private safelyResizeImage(hotel: Hotel) {
    const image: string = hotel.hotelImageUrls && hotel.hotelImageUrls.y;

    return image && this.props.thumbor
      ? this.props.thumbor.safelyGenerateThumborizedImageUrl(
          image,
          340,
          150,
          this.props.context,
          AspectRatioEnum.DEFAULT,
          true,
          100
        )
      : image;
  }

  public render() {
    const { flexTemplateNode, l10n, context, flexModuleModelStore } = this.props;
    const { id, options } = flexTemplateNode;
    const keyHelper = new ItemKeyHelper("hotels");
    const { formatText } = l10n;
    const model = flexModuleModelStore.getModel(id) as HotelsFlexModuleResult | null;

    if (!model || !model.hotels || model.hotels.length === 0) {
      return null;
    }

    const cardContent = (hotel: Hotel) => () => (
      <CardContent model={model} hotel={hotel} context={context} locale={context.locale} isViewCarousel={true} />
    );

    const hotelCardProps = (hotel: Hotel) => ({
      callToActionHotelName: model.callToActionHotelName,
      context,
      hotel,
      CardContent: cardContent(hotel),
      isMobile: true,
      showGuestRating: model.showGuestRating,
      showHeartButton: model.showHeartButton,
      locationName: model.locationName,
    });

    // Since uitk-carousel is only client-side, we need to generate the thumbor images server-side here
    model.hotels.forEach((hotel) => this.safelyResizeImage(hotel));

    // To be replaced by a more generic hotel card carousel
    const HotelCardCarousel = () => {
      const carouselProps = {
        itemsVisible: { lg: 1, md: 1, sm: 1 },
        pageBy: 1,
        peekWidth: 24,
        buttonText: {
          nextButton: formatText("carousel.item.next"),
          prevButton: formatText("carousel.item.prev"),
        },
      };

      const hotelAnalyticsId = "TG.LP.Dest.Hotels";
      /* istanbul ignore next */
      const scrolledPastSecondHotel = (moduleName: string) => {
        const hotelAnalyticsModuleName = `${hotelAnalyticsId}.Hotels.Carousel.itemViewed`;
        const firstHotelModuleName = `${hotelAnalyticsModuleName}.0`;
        const secondHotelModuleName = `${hotelAnalyticsModuleName}.1`;

        return moduleName !== firstHotelModuleName && moduleName !== secondHotelModuleName;
      };
      /* istanbul ignore next */
      const analytics: AnalyticsConfig = {
        id: hotelAnalyticsId,
        callback: (moduleId, description) => {
          const flexTrackingInfo: FlexTrackingInfo = {
            moduleName: moduleId,
            action: Action.SCROLL,
            linkName: description,
          };
          if (scrolledPastSecondHotel(moduleId)) {
            sendDelayedTrackEvent(flexTrackingInfo, this.props.analytics);
          }
        },
      };

      return (
        <UitkSpacing padding={{ blockend: "three" }}>
          <IsomorphicCarousel analytics={analytics} carouselName={"Hotels"} {...carouselProps} headline={model.title}>
            {model.hotels.map((hotel, hotelIndex) => (
              <HotelCard
                {...hotelCardProps(hotel)}
                key={keyHelper.next()}
                index={hotelIndex + 1}
                locationId={model.destinationId}
              />
            ))}
          </IsomorphicCarousel>
        </UitkSpacing>
      );
    };

    return (
      <HotelsContext.Provider value={{ context, flexTemplateNode, model }}>
        <SchemaOrgScript structuredData={hotelStructuredData(model.hotels)} />
        <Viewport>
          <ViewSmall>
            <div className="Hotels Hotels_ViewCarousel uitk-cell all-b-margin-six" id={id} data-fm={options.fmId}>
              <HotelCardCarousel />
              <ViewAllHotelsButton model={model} />
              <LegalText className="all-t-padding-three all-x-padding-three" />
            </div>
          </ViewSmall>
          <ViewLarge>
            <div className="Hotels uitk-cell all-cell-1-1 all-b-margin-six" id={id} data-fm={options.fmId}>
              <UitkHeading level={3} size={600}>
                {model.title}
              </UitkHeading>
              <UitkSpacing margin={{ blockend: "three" }}>
                <UitkLayoutGrid columns={3} space="three">
                  {model.hotels.map((hotel, hotelIndex) => (
                    <UitkLayoutGridItem key={keyHelper.next()}>
                      <HotelCard {...hotelCardProps(hotel)} index={hotelIndex + 1} locationId={model.destinationId} />
                    </UitkLayoutGridItem>
                  ))}
                </UitkLayoutGrid>
              </UitkSpacing>
              <ViewAllHotelsButton model={model} />
              <UitkSpacing padding={{ blockstart: "three" }}>
                <LegalText />
              </UitkSpacing>
            </div>
          </ViewLarge>
        </Viewport>
      </HotelsContext.Provider>
    );
  }
}

export default ViewCarousel;
